import { useNavigate } from "@tanstack/react-router";
import { ChevronDown, Pencil, Plus, Star, Trash2 } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { useDeleteSavedScreen } from "../../api/hooks/useDeleteSavedScreen";
import { useSavedScreens } from "../../api/hooks/useSavedScreens";
import { ScreenBuilderDialog } from "../screens/ScreenBuilderDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  PINNED_MAX,
  TABS_MAX,
  readPinnedIds,
  togglePinned,
  writePinnedIds,
} from "./pinnedFilters";

type SavedFilter = {
  saved_screen_id: string;
  name: string;
  criteria: Record<string, unknown>;
};

export type SavedFiltersTabStripProps = {
  activeScreenId: string | undefined;
};

export function SavedFiltersTabStrip({ activeScreenId }: SavedFiltersTabStripProps) {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useSavedScreens();
  const deleteMutation = useDeleteSavedScreen();
  const [pinnedIds, setPinnedIds] = useState<string[]>(() => readPinnedIds());
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState<SavedFilter | null>(null);
  const [pendingDelete, setPendingDelete] = useState<SavedFilter | null>(null);
  const overflowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!overflowOpen) return;
    function onClick(event: MouseEvent) {
      if (
        overflowRef.current &&
        event.target instanceof Node &&
        !overflowRef.current.contains(event.target)
      ) {
        setOverflowOpen(false);
      }
    }
    window.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("mousedown", onClick);
    };
  }, [overflowOpen]);

  const filters: SavedFilter[] = useMemo(
    () =>
      (data?.items ?? []).map((f) => ({
        saved_screen_id: f.saved_screen_id,
        name: f.name,
        criteria: f.criteria as Record<string, unknown>,
      })),
    [data],
  );

  // Re-order so pinned filters always lead, followed by everything else
  // in the API's natural order. Pinned set is bounded to PINNED_MAX (5)
  // and the tab strip caps at TABS_MAX (7) — the remainder spills into
  // the "More" overflow menu.
  const ordered = useMemo(() => {
    const pinnedSet = new Set(pinnedIds);
    const pinned = pinnedIds
      .map((id) => filters.find((f) => f.saved_screen_id === id))
      .filter((f): f is SavedFilter => f !== undefined);
    const rest = filters.filter((f) => !pinnedSet.has(f.saved_screen_id));
    return { pinned, rest };
  }, [filters, pinnedIds]);

  const visibleRest = ordered.rest.slice(0, Math.max(0, TABS_MAX - ordered.pinned.length));
  const overflow = ordered.rest.slice(visibleRest.length);

  // If the active filter is in overflow, pull it forward into the visible
  // tab strip so the user sees what's selected without opening the menu.
  const activeInOverflow = activeScreenId
    ? overflow.find((f) => f.saved_screen_id === activeScreenId)
    : undefined;
  const visible = activeInOverflow
    ? [...ordered.pinned, ...visibleRest.slice(0, -1), activeInOverflow]
    : [...ordered.pinned, ...visibleRest];

  function selectFilter(id: string | undefined) {
    void navigate({
      to: "/trades",
      search: id === undefined ? {} : { screen_id: id },
    });
  }

  function handleTogglePin(id: string) {
    setPinnedIds((prev) => {
      const next = togglePinned(prev, id);
      writePinnedIds(next);
      return next;
    });
  }

  async function confirmDelete() {
    if (pendingDelete === null) return;
    const id = pendingDelete.saved_screen_id;
    try {
      await deleteMutation.mutateAsync(id);
      // Drop from the pinned set if present.
      setPinnedIds((prev) => {
        if (!prev.includes(id)) return prev;
        const next = prev.filter((existing) => existing !== id);
        writePinnedIds(next);
        return next;
      });
      // If the deleted filter was active, clear ?screen_id.
      if (activeScreenId === id) {
        selectFilter(undefined);
      }
      setPendingDelete(null);
      setOverflowOpen(false);
    } catch {
      // The mutation surfaces its own error; keep the dialog open so the
      // operator can retry or cancel.
    }
  }

  return (
    <div
      role="tablist"
      aria-label="Saved filters"
      className="flex items-center gap-1 overflow-x-auto border-b border-outline-variant pb-1"
    >
      <FilterTab
        label="All"
        active={activeScreenId === undefined}
        onClick={() => {
          selectFilter(undefined);
        }}
      />
      {isLoading && (
        <span className="px-3 py-1.5 text-sm text-on-surface-muted" role="status">
          Loading…
        </span>
      )}
      {isError && (
        <span className="px-3 py-1.5 text-sm text-danger" role="alert">
          Filters unavailable
        </span>
      )}
      {visible.map((filter) => (
        <FilterTab
          key={filter.saved_screen_id}
          label={filter.name}
          active={activeScreenId === filter.saved_screen_id}
          pinned={pinnedIds.includes(filter.saved_screen_id)}
          canPin={
            pinnedIds.includes(filter.saved_screen_id) ||
            pinnedIds.length < PINNED_MAX
          }
          onClick={() => {
            selectFilter(filter.saved_screen_id);
          }}
          onEdit={() => {
            setEditing(filter);
          }}
          onTogglePin={() => {
            handleTogglePin(filter.saved_screen_id);
          }}
          onDelete={() => {
            setPendingDelete(filter);
          }}
        />
      ))}
      {overflow.length > 0 && (
        <div ref={overflowRef} className="relative">
          <button
            type="button"
            aria-label={`More filters (${overflow.length})`}
            aria-expanded={overflowOpen}
            onClick={() => {
              setOverflowOpen((prev) => !prev);
            }}
            className="inline-flex items-center gap-1 rounded-control border border-transparent px-3 py-1.5 text-sm font-medium text-on-surface-variant hover:bg-surface-container-high"
          >
            More
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          {overflowOpen && (
            <ul
              role="menu"
              className="absolute right-0 top-full z-30 mt-1 min-w-[200px] max-h-72 overflow-auto rounded-md border border-outline-variant bg-surface-container py-1 shadow-lg"
            >
              {overflow.map((filter) => {
                const isPinned = pinnedIds.includes(filter.saved_screen_id);
                const isActive = activeScreenId === filter.saved_screen_id;
                const canPin = isPinned || pinnedIds.length < PINNED_MAX;
                return (
                  <li
                    key={filter.saved_screen_id}
                    role="menuitem"
                    className={`flex items-center justify-between gap-2 px-3 py-1.5 text-sm hover:bg-surface-container-high ${isActive ? "bg-surface-container-high" : ""}`}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        selectFilter(filter.saved_screen_id);
                        setOverflowOpen(false);
                      }}
                      className="flex-1 truncate text-left text-on-surface"
                    >
                      {filter.name}
                    </button>
                    <button
                      type="button"
                      aria-label={`Edit ${filter.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setEditing(filter);
                        setOverflowOpen(false);
                      }}
                      className="shrink-0 rounded p-0.5 text-on-surface-muted hover:text-primary"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      aria-label={
                        isPinned ? `Unpin ${filter.name}` : `Pin ${filter.name}`
                      }
                      disabled={!canPin}
                      onClick={(event) => {
                        event.stopPropagation();
                        handleTogglePin(filter.saved_screen_id);
                      }}
                      className="shrink-0 rounded p-0.5 text-on-surface-muted hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      <Star
                        className={`h-3.5 w-3.5 ${isPinned ? "fill-primary text-primary" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${filter.name}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setPendingDelete(filter);
                      }}
                      className="shrink-0 rounded p-0.5 text-on-surface-muted hover:text-danger"
                    >
                      <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => {
          setBuilderOpen(true);
        }}
        className="inline-flex items-center gap-1 rounded-control border border-dashed border-outline-variant px-3 py-1.5 text-sm font-medium text-on-surface-variant hover:border-primary hover:bg-surface-container-high hover:text-on-surface"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        New filter
      </button>
      <ScreenBuilderDialog open={builderOpen} onOpenChange={setBuilderOpen} />
      <ScreenBuilderDialog
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
        editing={editing}
      />
      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete saved filter?</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &lsquo;{pendingDelete?.name ?? ""}&rsquo;? This action
              can&apos;t be undone from the trades page.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteMutation.isError && (
            <p role="alert" className="text-sm text-danger">
              Failed to delete. Try again.
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={deleteMutation.isPending}
              onClick={(event) => {
                event.preventDefault();
                void confirmDelete();
              }}
              className="bg-danger text-on-danger hover:bg-danger/90"
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type FilterTabProps = {
  label: string;
  active: boolean;
  pinned?: boolean;
  canPin?: boolean;
  onClick: () => void;
  onEdit?: () => void;
  onTogglePin?: () => void;
  onDelete?: () => void;
};

function FilterTab({
  label,
  active,
  pinned = false,
  canPin = true,
  onClick,
  onEdit,
  onTogglePin,
  onDelete,
}: FilterTabProps) {
  // If a filter is already pinned, the star stays visible permanently so
  // the user always sees the pin state. The actions cluster (star +
  // delete) only slides in from the right when the tab is hovered or
  // focused — animating the cluster's max-width from 0 to a sufficient
  // bound creates the slide effect without transforms / overflow tricks.
  const hasActions = Boolean(onEdit || onTogglePin || onDelete);
  return (
    <div
      role="tab"
      aria-selected={active}
      className={`group relative inline-flex shrink-0 items-center rounded-control border ${
        active
          ? "border-primary bg-surface-container-high text-on-surface"
          : "border-transparent text-on-surface-variant hover:bg-surface-container-high hover:text-on-surface"
      }`}
    >
      <button
        type="button"
        onClick={onClick}
        className="px-3 py-1.5 text-sm font-medium"
      >
        {label}
      </button>
      {hasActions && (
        <span
          className={`flex items-center overflow-hidden transition-[max-width,opacity] duration-150 ease-out ${
            pinned
              ? "max-w-[90px] opacity-100"
              : "max-w-0 opacity-0 group-hover:max-w-[90px] group-hover:opacity-100 group-focus-within:max-w-[90px] group-focus-within:opacity-100"
          }`}
        >
          {onEdit && (
            <button
              type="button"
              aria-label={`Edit ${label}`}
              onClick={(event) => {
                event.stopPropagation();
                onEdit();
              }}
              className="rounded p-0.5 text-on-surface-muted hover:text-primary"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
          {onTogglePin && (
            <button
              type="button"
              aria-label={pinned ? `Unpin ${label}` : `Pin ${label}`}
              disabled={!pinned && !canPin}
              onClick={(event) => {
                event.stopPropagation();
                onTogglePin();
              }}
              className="rounded p-0.5 text-on-surface-muted hover:text-primary disabled:cursor-not-allowed disabled:opacity-30"
              aria-pressed={pinned}
            >
              <Star
                className={`h-3.5 w-3.5 ${pinned ? "fill-primary text-primary" : ""}`}
                aria-hidden="true"
              />
            </button>
          )}
          {onDelete && (
            <button
              type="button"
              aria-label={`Delete ${label}`}
              onClick={(event) => {
                event.stopPropagation();
                onDelete();
              }}
              className="mr-1 rounded p-0.5 text-on-surface-muted hover:text-danger"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          )}
        </span>
      )}
    </div>
  );
}
